import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../lib/i18n";

type Props = { children: ReactNode; message: { title: string; body: string; reload: string; home: string } };

class Boundary extends Component<Props, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Seva Sangrah page error", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const m = this.props.message;
    return (
      <div className="mx-auto max-w-xl px-5 py-24 text-center" role="alert">
        <h1 className="font-display text-3xl text-ink-900">{m.title}</h1>
        <p className="mt-4 text-[15px] leading-relaxed text-ink-600">{m.body}</p>
        <div className="mt-8 flex justify-center gap-3">
          <button onClick={() => location.reload()} className="cursor-pointer rounded-full bg-ink-900 px-5 py-2.5 text-sm font-semibold text-white">
            {m.reload}
          </button>
          <a href="/" className="rounded-full border border-ink-200 px-5 py-2.5 text-sm font-semibold text-ink-800">
            {m.home}
          </a>
        </div>
        {/* Shown so a screenshot carries enough detail to diagnose the problem. */}
        <p className="mt-10 break-words font-mono text-[11px] text-ink-400">
          {error.name}: {error.message}
        </p>
      </div>
    );
  }
}

/** Shows a readable message instead of a blank screen when a page fails, and resets on navigation. */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { tx } = useLang();
  const message = {
    title: tx("This page didn't load properly", "यह पेज ठीक से नहीं खुला"),
    body: tx(
      "Please reload the page. If it keeps happening, send us a screenshot of this screen.",
      "कृपया पेज को दोबारा लोड करें। अगर बार-बार ऐसा हो, तो इस स्क्रीन का स्क्रीनशॉट हमें भेज दीजिए।",
    ),
    reload: tx("Reload page", "पेज दोबारा लोड करें"),
    home: tx("Go to home page", "होम पेज पर जाएँ"),
  };
  return (
    <Boundary key={pathname} message={message}>
      {children}
    </Boundary>
  );
}
